import { useState, useRef, useCallback, useEffect } from "react";
import { ChevronLeft, ChevronRight, Sparkles, Share2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { shareLook } from "../lib/tryOnData";

// A credit-free demo: shows a fixed "before" photo, and on "Try it on"
// reveals a pre-generated "after" of the same person in a vintage garment.
export default function DemoTryOn({ before, after, shareKey, shareLabel, aspect = "3 / 4" }) {
  const [stage, setStage] = useState("idle"); // idle | rendering | done
  const [pos, setPos] = useState(52);
  const ref = useRef(null);
  const dragging = useRef(false);

  useEffect(() => {
    setStage("idle");
    setPos(52);
  }, [before, after]);

  const run = () => {
    setStage("rendering");
    setTimeout(() => setStage("done"), 1700);
  };

  const setFromClientX = useCallback((clientX) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let p = ((clientX - rect.left) / rect.width) * 100;
    p = Math.max(0, Math.min(100, p));
    setPos(p);
  }, []);

  useEffect(() => {
    const move = (e) => {
      if (!dragging.current) return;
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      setFromClientX(x);
    };
    const stop = () => {
      dragging.current = false;
    };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", stop);
    document.addEventListener("touchmove", move, { passive: true });
    document.addEventListener("touchend", stop);
    return () => {
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", stop);
      document.removeEventListener("touchmove", move);
      document.removeEventListener("touchend", stop);
    };
  }, [setFromClientX]);

  const start = (e) => {
    if (stage !== "done") return;
    dragging.current = true;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    setFromClientX(x);
  };

  const onShare = async (e) => {
    e.stopPropagation();
    const r = await shareLook(shareKey, shareLabel);
    if (r.copied) toast.success("Link copied — paste it anywhere.");
  };

  return (
    <div className="w-full">
      <div
        ref={ref}
        data-testid="demo-tryon"
        onMouseDown={start}
        onTouchStart={start}
        style={{ touchAction: "pan-y", aspectRatio: aspect }}
        className={`relative w-full overflow-hidden rounded-[14px] select-none ring-1 ring-[#1A1A1A]/10 shadow-[0_40px_90px_-30px_rgba(26,20,15,0.5)] bg-[#EFE7DC] ${
          stage === "done" ? "cursor-ew-resize" : ""
        }`}
      >
        {/* AFTER (dressed) fills the frame */}
        <img src={after} alt="Wearing the piece" className="absolute inset-0 w-full h-full object-cover object-top pointer-events-none" />

        {/* BEFORE (plain) — full until done, then clipped by handle */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ clipPath: stage === "done" ? `inset(0 ${100 - pos}% 0 0)` : "inset(0 0 0 0)" }}
        >
          <img src={before} alt="Original" className="w-full h-full object-cover object-top" />
        </div>

        {/* Rendering shimmer */}
        <AnimatePresence>
          {stage === "rendering" && (
            <motion.div
              key="rendering"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 flex flex-col items-center justify-center bg-[#1A140F]/45 backdrop-blur-[2px]"
            >
              <div className="w-10 h-10 border-2 border-[#FBF7F0]/40 border-t-[#E8C89B] rounded-full animate-spin mb-4" />
              <p className="font-serif-display text-xl italic text-[#FBF7F0]">Tailoring your fitting…</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Idle CTA */}
        {stage === "idle" && (
          <div className="absolute inset-0 flex flex-col items-center justify-end pb-8 bg-gradient-to-t from-[#1A140F]/60 via-transparent to-transparent">
            <button
              data-testid="demo-tryon-button"
              onClick={run}
              className="group inline-flex items-center gap-2.5 bg-[#FBF7F0] text-[#1A140F] font-medium px-7 py-3.5 rounded-full hover:bg-[#E8C89B] transition-colors duration-300 shadow-lg"
            >
              <Sparkles className="w-4 h-4" />
              Try it on
            </button>
            <span className="mt-3 rounded-full bg-[#1A140F]/70 backdrop-blur px-3 py-1 text-[9px] uppercase tracking-[0.25em] text-[#FBF7F0]">
              Example preview · no photo needed
            </span>
          </div>
        )}

        {/* Done: labels + drag handle */}
        {stage === "done" && (
          <>
            <span className="absolute bottom-4 left-4 rounded-full bg-[#1A140F]/75 backdrop-blur px-3 py-1 text-[9px] uppercase tracking-[0.25em] text-[#FBF7F0] pointer-events-none">
              Original
            </span>
            <span className="absolute bottom-4 right-4 rounded-full bg-[#6D214F]/85 backdrop-blur px-3 py-1 text-[9px] uppercase tracking-[0.25em] text-[#FBF7F0] pointer-events-none">
              Try-On
            </span>
            <span className="absolute top-4 left-1/2 -translate-x-1/2 rounded-full bg-[#FBF7F0]/85 backdrop-blur px-4 py-1.5 text-[9px] uppercase tracking-[0.28em] text-[#1A140F] pointer-events-none">
              Drag to reveal
            </span>
            <div className="absolute inset-y-0 z-10 pointer-events-none" style={{ left: `${pos}%`, transform: "translateX(-50%)" }}>
              <div className="w-[2px] h-full bg-[#FBF7F0]/90 shadow-[0_0_10px_rgba(0,0,0,0.35)]" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-[#FBF7F0] flex items-center justify-center shadow-xl ring-1 ring-[#1A140F]/10">
                <ChevronLeft className="w-4 h-4 text-[#1A140F] -mr-1" strokeWidth={2.2} />
                <ChevronRight className="w-4 h-4 text-[#1A140F] -ml-1" strokeWidth={2.2} />
              </div>
            </div>
          </>
        )}
      </div>

      {stage === "done" && (
        <div className="mt-3 flex items-center justify-center gap-4">
          <p className="text-xs text-[#4A4A4A]">
            A sample fitting — the same model, restyled.
          </p>
          <button
            onClick={onShare}
            data-testid="demo-share"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-[#6D214F] hover:text-[#1A140F] transition-colors"
          >
            <Share2 className="w-3.5 h-3.5" /> Share this look
          </button>
        </div>
      )}
    </div>
  );
}
